import { uploadLayer, pollUntilReady } from '../api/layers.js';
import { auth } from '../auth/state.js';
import { toast } from './toast.js';
import { refreshMyLayers, showLayerOnMap } from './myLayersPanel.js';
import { openAuthModal } from '../auth/modal.js';
import { openUploadMetaModal } from './uploadMetaModal.js';

const ACCEPTED = '.zip,.geojson,.json,.csv,.tif,.tiff';

export function initUploadDialog() {
  const dropZone = document.getElementById('uploadDrop');
  if (!dropZone) return;

  dropZone.addEventListener('click', () => {
    if (!auth.isAuthenticated()) { openAuthModal(); return; }
    pickFile();
  });

  dropZone.addEventListener('dragover', e => {
    e.preventDefault();
    dropZone.style.borderColor = 'var(--green)';
  });
  dropZone.addEventListener('dragleave', () => { dropZone.style.borderColor = ''; });
  dropZone.addEventListener('drop', async e => {
    e.preventDefault();
    dropZone.style.borderColor = '';
    if (!auth.isAuthenticated()) { openAuthModal(); return; }
    const file = e.dataTransfer.files?.[0];
    if (file) await handleFile(file);
  });
}

function pickFile() {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = ACCEPTED;
  input.onchange = async () => {
    const file = input.files?.[0];
    if (file) await handleFile(file);
  };
  input.click();
}

let activeCancel = null;   // cancel fn for the in-flight upload, if any

async function handleFile(file) {
  const meta = await openUploadMetaModal(file);
  if (!meta) return;   // user cancelled

  const { name, category, description } = meta;
  setUploadingState(file.name, 0);

  const { promise, cancel } = uploadLayer({
    file, name, category, description,
    onProgress: pct => setUploadingState(file.name, pct),
  });
  activeCancel = cancel;

  try {
    const layer = await promise;
    activeCancel = null;
    setProcessingState(file.name);
    const ready = await pollUntilReady(layer.id);
    clearStatus();
    toast(
      ready.layer_type === 'raster'
        ? `Raster "${name}" stored — ${ready.band_count} band(s)`
        : `"${name}" ready — ${ready.feature_count} features`
    );
    await refreshMyLayers();
    // Auto-show on the map so the user immediately sees what they uploaded.
    try { await showLayerOnMap(ready.id); } catch (e) { console.warn('Auto-show failed', e); }
  } catch (e) {
    activeCancel = null;
    clearStatus();
    if (!e.aborted) toast(`Upload failed: ${e.message}`);
  }
}

export function cancelActiveUpload() {
  if (!activeCancel) return;
  activeCancel();
  activeCancel = null;
  clearStatus();
  toast('Upload cancelled');
}

function setUploadingState(filename, pct) {
  const el = document.getElementById('uploadStatus');
  if (!el) return;
  const indeterminate = pct < 0;
  const w = indeterminate ? 100 : Math.min(100, Math.max(2, pct));
  const label = indeterminate
    ? `Uploading ${filename}…`
    : `Uploading ${filename} · ${w.toFixed(0)}%`;
  el.style.display = 'block';
  el.innerHTML = `
    <div style="display:flex;justify-content:space-between;align-items:baseline;gap:10px;font-size:11.5px;color:var(--text2)">
      <span style="flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${escapeHtml(label)}</span>
      <button data-action="cancelUpload" title="Cancel"
              style="border:none;background:transparent;color:var(--text3);cursor:pointer;font-family:var(--font);font-size:11px;font-weight:600;letter-spacing:.04em">
        Cancel
      </button>
    </div>
    <div style="margin-top:6px;height:3px;background:var(--bg5);border-radius:2px;overflow:hidden">
      <div style="height:100%;width:${w}%;background:var(--primary);transition:width .15s linear${indeterminate ? ';animation:upl-shimmer 1.4s infinite linear' : ''}"></div>
    </div>
  `;
}

function setProcessingState(filename) {
  const el = document.getElementById('uploadStatus');
  if (!el) return;
  el.innerHTML = `
    <div style="font-size:11.5px;color:var(--text2)">
      Server is ingesting <em>${escapeHtml(filename)}</em>… this can take a moment for big files.
    </div>
    <div style="margin-top:6px;height:3px;background:var(--bg5);border-radius:2px;overflow:hidden">
      <div class="skel" style="height:100%;width:100%"></div>
    </div>
  `;
}

function clearStatus() {
  const el = document.getElementById('uploadStatus');
  if (!el) return;
  el.style.display = 'none';
  el.innerHTML = '';
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"]/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;' }[c]));
}
